import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { pricingApi, stationsApi } from '../services/api';
import {
  QUIKQ_BASE,
  STATION_ENDPOINTS,
  newRowId,
  defaultQueryRows,
  buildFullUrl,
} from '../utils/quikqStationSearch';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const PriceOverrides = () => {
  const [overrides, setOverrides] = useState([]);
  const [summary, setSummary] = useState(null);
  const [listTotal, setListTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  /** 0 = list, 1 = add override (full page) */
  const [uiTab, setUiTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStationCode, setFilterStationCode] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [searchTab, setSearchTab] = useState(0);
  const [endpointPath, setEndpointPath] = useState('/carriers/stations');
  const [queryRows, setQueryRows] = useState(defaultQueryRows);
  const [rawUrlOverride, setRawUrlOverride] = useState('');
  const [quikqToken, setQuikqToken] = useState('');
  const [quikqUserId, setQuikqUserId] = useState('');
  const [quikqPassword, setQuikqPassword] = useState('');
  const [quikqStartCode, setQuikqStartCode] = useState('');
  const [clientListFilter, setClientListFilter] = useState('');
  const [quikqConfigured, setQuikqConfigured] = useState(null);

  const [searchResults, setSearchResults] = useState([]);
  /** Same metadata shape as Stations search (endpoint, auth, counts). */
  const [quikqSearchMeta, setQuikqSearchMeta] = useState(null);
  const [expandedStationRows, setExpandedStationRows] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  /** Reference price from QuikQ repricing for the selected station+product */
  const [referencePrice, setReferencePrice] = useState(null);

  const [formData, setFormData] = useState({
    stationCode: '',
    stationName: '',
    prodCode: '',
    fuelName: '',
    overridePrice: '',
    reason: '',
    effectiveFrom: '',
    effectiveTo: '',
  });

  const fetchSummary = useCallback(async () => {
    try {
      const data = await pricingApi.getPriceOverridesSummary();
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      const data = await pricingApi.listPriceOverrideProducts();
      setProductOptions(data.products || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const fetchOverrides = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pricingApi.listPriceOverrides({
        limit: rowsPerPage,
        skip: page * rowsPerPage,
        stationCode: filterStationCode || undefined,
        isActive: filterActive,
      });
      setOverrides(data.overrides || []);
      setListTotal(typeof data.total === 'number' ? data.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch price overrides');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterStationCode, filterActive]);

  useEffect(() => {
    fetchSummary();
    loadProducts();
  }, [fetchSummary, loadProducts]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  useEffect(() => {
    if (uiTab !== 1) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await stationsApi.getConfigStatus();
        if (!cancelled) setQuikqConfigured(!!data.configured);
      } catch {
        if (!cancelled) setQuikqConfigured(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uiTab]);

  const previewUrl = useMemo(() => {
    if (rawUrlOverride.trim()) return rawUrlOverride.trim();
    return buildFullUrl(endpointPath, queryRows);
  }, [endpointPath, queryRows, rawUrlOverride]);

  const filteredStationResults = useMemo(() => {
    const list = searchResults;
    if (!Array.isArray(list)) return [];
    const needle = clientListFilter.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((s) => {
      const hay = [s.address, s.stationName, s.store, s.city, s.state, s.zip, s.stationCode]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [searchResults, clientListFilter]);

  const updateQueryRow = (id, field, value) => {
    setQueryRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const addQueryRow = () => {
    setQueryRows((rows) => [...rows, { id: newRowId(), key: '', value: '', description: '' }]);
  };

  const removeQueryRow = (id) => {
    setQueryRows((rows) => rows.filter((r) => r.id !== id));
  };

  const resetQueryTemplate = () => setQueryRows(defaultQueryRows());

  const handleQuikqSend = async (e) => {
    e?.preventDefault();
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);
    setQuikqSearchMeta(null);
    setExpandedStationRows({});
    const quikqPostmanUrl = rawUrlOverride.trim() || previewUrl;
    const hasToken = quikqToken.trim().length > 0;
    const hasInlineQuikq = quikqUserId.trim() && quikqPassword && quikqStartCode.trim();
    const body = {
      quikqPostmanUrl,
      ...(hasToken ? { quikqToken: quikqToken.trim() } : {}),
      ...(hasInlineQuikq && !hasToken
        ? {
            quikqUserId: quikqUserId.trim(),
            quikqPassword,
            quikqStartCode: quikqStartCode.trim(),
          }
        : {}),
    };
    try {
      const data = await pricingApi.searchQuikqForPriceOverride(body);
      const stations = data.stations || [];
      setSearchResults(stations);
      setQuikqSearchMeta({
        endpoint: data.endpoint || '',
        mode: data.mode || '',
        authMode: data.authMode || '',
        count: typeof data.count === 'number' ? data.count : stations.length,
        totalFromApi: data.totalFromApi,
      });
      setExpandedStationRows({});
    } catch (err) {
      const apiMsg = err.response?.data?.message || err.response?.data?.error;
      setSearchError(apiMsg || err.message || 'Lookup failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const authHasToken = quikqToken.trim().length > 0;
  const authInline = quikqUserId.trim() && quikqPassword && quikqStartCode.trim();
  const canSendQuikq = quikqConfigured === true || authInline || authHasToken;

  const applyStationFuelSelection = (station, fuel) => {
    const code = String(station.stationCode || '').trim();
    const name = String(station.stationName || station.store || code || '').trim();
    if (fuel) {
      setFormData((prev) => ({
        ...prev,
        stationCode: code,
        stationName: name,
        prodCode: fuel.prodCode,
        fuelName: fuel.fuelName,
      }));
      setReferencePrice(
        typeof fuel.currentPrice === 'number' && Number.isFinite(fuel.currentPrice)
          ? fuel.currentPrice
          : null
      );
    } else {
      setFormData((prev) => ({
        ...prev,
        stationCode: code,
        stationName: name,
        prodCode: '',
        fuelName: '',
      }));
      setReferencePrice(null);
    }
  };

  const toggleStationRow = (rowKey) => {
    setExpandedStationRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const formatMoney = (v) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return `$${n.toFixed(3)}`;
  };

  const resetAddOverrideState = useCallback(() => {
    setSearchTab(0);
    setEndpointPath('/carriers/stations');
    setQueryRows(defaultQueryRows());
    setRawUrlOverride('');
    setQuikqToken('');
    setQuikqUserId('');
    setQuikqPassword('');
    setQuikqStartCode('');
    setClientListFilter('');
    setSearchResults([]);
    setSearchError(null);
    setReferencePrice(null);
    setQuikqSearchMeta(null);
    setExpandedStationRows({});
    setFormData({
      stationCode: '',
      stationName: '',
      prodCode: '',
      fuelName: '',
      overridePrice: '',
      reason: '',
      effectiveFrom: '',
      effectiveTo: '',
    });
  }, []);

  const handleOpenDialog = (override = null) => {
    if (override) {
      setEditingOverride(override);
      setFormData({
        stationCode: override.stationCode || '',
        stationName: override.stationName || '',
        prodCode: override.prodCode || '',
        fuelName: override.fuelName || '',
        overridePrice: override.overridePrice || '',
        reason: override.reason || '',
        effectiveFrom: override.effectiveFrom ? new Date(override.effectiveFrom).toISOString().slice(0, 16) : '',
        effectiveTo: override.effectiveTo ? new Date(override.effectiveTo).toISOString().slice(0, 16) : '',
      });
      setReferencePrice(null);
      setEditDialogOpen(true);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingOverride(null);
    setFormData({
      stationCode: '',
      stationName: '',
      prodCode: '',
      fuelName: '',
      overridePrice: '',
      reason: '',
      effectiveFrom: '',
      effectiveTo: '',
    });
  };

  const handleOpenAddTab = () => {
    resetAddOverrideState();
    setUiTab(1);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveOverride = async () => {
    try {
      setError(null);
      if (!formData.stationCode) {
        setError('Search QuikQ and select a station (and fuel row when prices are listed).');
        return;
      }
      if (!formData.prodCode) {
        setError('Select a fuel product.');
        return;
      }
      const productExists = productOptions.some((p) => p.prodCode === formData.prodCode);
      if (!productExists) {
        setError('Selected product is not supported in the fuel app');
        return;
      }
      const parsedPrice = parseFloat(formData.overridePrice);
      if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
        setError('Override price must be a non-negative number');
        return;
      }

      const payload = {
        stationCode: formData.stationCode,
        stationName: formData.stationName,
        prodCode: formData.prodCode,
        fuelName: formData.fuelName,
        overridePrice: parsedPrice,
        reason: formData.reason,
        effectiveFrom: formData.effectiveFrom ? new Date(formData.effectiveFrom).toISOString() : undefined,
        effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo).toISOString() : undefined,
      };

      if (editingOverride) {
        await pricingApi.updatePriceOverride(formData.stationCode, formData.prodCode, payload);
        setSuccess('Price override updated successfully');
        handleCloseEditDialog();
      } else {
        await pricingApi.createPriceOverride(payload);
        setSuccess('Price override created successfully');
        resetAddOverrideState();
        setUiTab(0);
      }

      await fetchOverrides();
      await fetchSummary();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save price override');
    }
  };

  const handleDeleteOverride = async (stationCode, prodCode) => {
    if (window.confirm('Are you sure you want to delete this price override?')) {
      try {
        setError(null);
        await pricingApi.deletePriceOverride(stationCode, prodCode);
        setSuccess('Price override deleted successfully');
        await fetchOverrides();
        await fetchSummary();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err.message || 'Failed to delete price override');
      }
    }
  };

  const handleDeactivateOverride = async (override) => {
    try {
      setError(null);
      await pricingApi.deactivatePriceOverride(override.stationCode, override.prodCode);
      setSuccess('Price override deactivated successfully');
      await fetchOverrides();
      await fetchSummary();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to deactivate price override');
    }
  };

  const handleReactivateOverride = async (override) => {
    try {
      setError(null);
      await pricingApi.reactivatePriceOverride(override.stationCode, override.prodCode);
      setSuccess('Price override reactivated successfully');
      await fetchOverrides();
      await fetchSummary();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reactivate price override');
    }
  };

  const isOverrideExpired = (override) => {
    if (!override.effectiveTo) return false;
    return new Date(override.effectiveTo) < new Date();
  };

  const isOverrideFuture = (override) => {
    if (!override.effectiveFrom) return false;
    return new Date(override.effectiveFrom) > new Date();
  };

  const getStatusChip = (override) => {
    if (!override.isActive) {
      return <Chip label="Inactive" color="default" size="small" />;
    }
    if (isOverrideFuture(override)) {
      return <Chip label="Pending" color="info" size="small" />;
    }
    if (isOverrideExpired(override)) {
      return <Chip label="Expired" color="warning" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  const overrideDetailsFields = (
    <>
      <Typography variant="subtitle2">Override details</Typography>
      <TextField
        label="Station code"
        value={formData.stationCode}
        fullWidth
        disabled
        size="small"
      />
      <TextField
        label="Station name"
        name="stationName"
        value={formData.stationName}
        onChange={handleFormChange}
        fullWidth
        size="small"
        disabled={!editingOverride && !formData.stationCode}
      />
      {!editingOverride && referencePrice != null && (
        <Alert severity="success">
          QuikQ catalog price for this fuel: <strong>${referencePrice.toFixed(3)}</strong>
        </Alert>
      )}
      <Autocomplete
        options={productOptions}
        loading={productsLoading}
        disabled={!!editingOverride}
        getOptionLabel={(option) => `${option.prodCode} - ${option.fuelName}`}
        isOptionEqualToValue={(option, value) => option.prodCode === value.prodCode}
        value={productOptions.find((p) => p.prodCode === formData.prodCode) || null}
        onChange={(event, newValue) => {
          setFormData((prev) => ({
            ...prev,
            prodCode: newValue?.prodCode || '',
            fuelName: newValue?.fuelName || '',
          }));
          if (!newValue) setReferencePrice(null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Fuel App Product"
            placeholder="Select product code"
            size="small"
            helperText={editingOverride ? 'Product code cannot be changed' : 'Required if search row had no fuel pricing'}
          />
        )}
      />
      <TextField label="Fuel Type Name" name="fuelName" value={formData.fuelName} fullWidth disabled size="small" />
      <TextField
        label="Override Price ($)"
        name="overridePrice"
        type="number"
        value={formData.overridePrice}
        onChange={handleFormChange}
        fullWidth
        size="small"
        inputProps={{ step: '0.001', min: '0' }}
        placeholder="0.000"
      />
      <TextField
        label="Reason"
        name="reason"
        value={formData.reason}
        onChange={handleFormChange}
        fullWidth
        multiline
        rows={2}
        placeholder="e.g., Temporary promotion, Contract rate"
      />
      <TextField
        label="Effective From"
        name="effectiveFrom"
        type="datetime-local"
        value={formData.effectiveFrom}
        onChange={handleFormChange}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Effective To (Optional)"
        name="effectiveTo"
        type="datetime-local"
        value={formData.effectiveTo}
        onChange={handleFormChange}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
      />
    </>
  );

  return (
    <Container maxWidth="lg" sx={{ py: uiTab === 1 ? 2 : 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Station Price Overrides
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => { fetchOverrides(); fetchSummary(); }} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {uiTab === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddTab}>
              Add Override
            </Button>
          )}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 3 }}>
        <Tabs
          value={uiTab}
          onChange={(_, v) => setUiTab(v)}
          variant="fullWidth"
          sx={{ px: 1 }}
          aria-label="Price overrides sections"
        >
          <Tab label="Overrides list" />
          <Tab label="Add override" />
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {uiTab === 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Overrides apply only to stations returned by QuikQ. Search by station code, name, or address (with ZIP or
            city/state, or coordinates). Results show live catalog fuel prices from QuikQ repricing; pick a row, then
            enter the override price.
          </Typography>

      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography color="textSecondary" variant="body2">
              Total Overrides
            </Typography>
            <Typography variant="h5" sx={{ mt: 1 }}>
              {summary.total}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
            <Typography color="textSecondary" variant="body2">
              Active
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, color: '#2e7d32' }}>
              {summary.active}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
            <Typography color="textSecondary" variant="body2">
              Expired
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, color: '#f57c00' }}>
              {summary.expired}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, bgcolor: '#f3e5f5' }}>
            <Typography color="textSecondary" variant="body2">
              Inactive
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, color: '#7b1fa2' }}>
              {summary.inactive}
            </Typography>
          </Paper>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Filter by Station Code"
            value={filterStationCode}
            onChange={(e) => {
              setFilterStationCode(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterActive === true}
                onChange={(e) => {
                  setFilterActive(e.target.checked ? true : null);
                  setPage(0);
                }}
              />
            }
            label="Active Only"
          />
          {(filterStationCode || filterActive !== null) && (
            <Button
              variant="outlined"
              onClick={() => {
                setFilterStationCode('');
                setFilterActive(null);
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell><strong>Station Code</strong></TableCell>
                  <TableCell><strong>Station Name</strong></TableCell>
                  <TableCell><strong>Fuel Type</strong></TableCell>
                  <TableCell align="right"><strong>Override Price</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Effective</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overrides.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        No price overrides found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  overrides.map((override) => (
                    <TableRow key={`${override.stationCode}-${override.prodCode}`} hover>
                      <TableCell>{override.stationCode}</TableCell>
                      <TableCell>{override.stationName}</TableCell>
                      <TableCell>{override.fuelName}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          ${override.overridePrice.toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell>{override.reason}</TableCell>
                      <TableCell>{getStatusChip(override)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(override.effectiveFrom).toLocaleDateString()}
                        </Typography>
                        {override.effectiveTo && (
                          <Typography variant="body2" color="textSecondary">
                            to {new Date(override.effectiveTo).toLocaleDateString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(override)}
                            sx={{ color: '#1976d2' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {override.isActive ? (
                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              onClick={() => handleDeactivateOverride(override)}
                              sx={{ color: '#f57c00' }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reactivate">
                            <IconButton
                              size="small"
                              onClick={() => handleReactivateOverride(override)}
                              sx={{ color: '#2e7d32' }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteOverride(override.stationCode, override.prodCode)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={listTotal}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </TableContainer>
        </>
      )}

      {uiTab === 1 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setUiTab(0)}
          variant="text"
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to overrides list
        </Button>
        <Typography variant="h5" fontWeight={600}>
          Add price override
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Same GET workflow as the <strong>Stations</strong> page: build the request below, <strong>Send</strong>, expand a
          station for repricing, then complete override details at the bottom.
        </Typography>


              {quikqConfigured === false && !authInline && !authHasToken && (
                <Alert severity="info">
                  No QuikQ env on server. Open the <strong>Authorization</strong> tab and enter a Bearer token or QuikQ login.
                </Alert>
              )}

              <Paper
                component="form"
                onSubmit={handleQuikqSend}
                elevation={2}
                sx={{ mb: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'grey.100',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Chip label="GET" color="success" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', flexShrink: 0 }}>
                    {QUIKQ_BASE}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 220, flex: '1 1 200px' }}>
                    <InputLabel id="po-endpoint-label">Path</InputLabel>
                    <Select
                      labelId="po-endpoint-label"
                      label="Path"
                      value={endpointPath}
                      onChange={(e) => setEndpointPath(e.target.value)}
                      disabled={!!rawUrlOverride.trim()}
                    >
                      {STATION_ENDPOINTS.map((opt) => (
                        <MenuItem key={opt.path} value={opt.path}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={searchLoading || !canSendQuikq}
                    startIcon={searchLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    sx={{ minWidth: 100 }}
                  >
                    {searchLoading ? 'Sending…' : 'Send'}
                  </Button>
                </Box>

                <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Full URL (preview)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      wordBreak: 'break-all',
                      color: 'text.primary',
                    }}
                  >
                    {previewUrl}
                  </Typography>
                </Box>

                <Box sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Tabs value={searchTab} onChange={(_, v) => setSearchTab(v)} variant="scrollable" scrollButtons="auto">
                    <Tab label="Query Params" />
                    <Tab label="Authorization" />
                    <Tab label="Advanced" />
                  </Tabs>
                </Box>

                <Box sx={{ px: 2, pb: 2 }}>
                  <TabPanel value={searchTab} index={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Query Params</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={resetQueryTemplate}>
                          Suggested template
                        </Button>
                        <Button size="small" startIcon={<AddIcon />} onClick={addQueryRow}>
                          Add row
                        </Button>
                      </Box>
                    </Box>
                    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell width="22%">Key</TableCell>
                            <TableCell width="28%">Value</TableCell>
                            <TableCell width="42%">Description</TableCell>
                            <TableCell width="8%" align="right" />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {queryRows.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell sx={{ py: 0.5, verticalAlign: 'top' }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Key"
                                  value={row.key}
                                  onChange={(e) => updateQueryRow(row.id, 'key', e.target.value)}
                                  disabled={!!rawUrlOverride.trim()}
                                  variant="standard"
                                  InputProps={{ disableUnderline: false }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5, verticalAlign: 'top' }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Value"
                                  value={row.value}
                                  onChange={(e) => updateQueryRow(row.id, 'value', e.target.value)}
                                  disabled={!!rawUrlOverride.trim()}
                                  variant="standard"
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5, verticalAlign: 'top' }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Description"
                                  value={row.description}
                                  onChange={(e) => updateQueryRow(row.id, 'description', e.target.value)}
                                  disabled={!!rawUrlOverride.trim()}
                                  variant="standard"
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                <Tooltip title="Delete row">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => removeQueryRow(row.id)}
                                      disabled={!!rawUrlOverride.trim()}
                                      aria-label="delete row"
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </TabPanel>

                  <TabPanel value={searchTab} index={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Bearer token
                    </Typography>
                    <TextField
                      label="QuikQ JWT / Bearer token"
                      fullWidth
                      size="small"
                      multiline
                      minRows={3}
                      maxRows={10}
                      autoComplete="off"
                      value={quikqToken}
                      onChange={(e) => setQuikqToken(e.target.value)}
                      placeholder="eyJ… (optional Bearer prefix)"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="subtitle2" gutterBottom>
                      Or QuikQ login (if you have no token)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <TextField
                        label="Username"
                        size="small"
                        value={quikqUserId}
                        onChange={(e) => setQuikqUserId(e.target.value)}
                        sx={{ flex: '1 1 180px' }}
                      />
                      <TextField
                        label="Password"
                        type="password"
                        size="small"
                        value={quikqPassword}
                        onChange={(e) => setQuikqPassword(e.target.value)}
                        sx={{ flex: '1 1 180px' }}
                      />
                      <TextField
                        label="Start code"
                        size="small"
                        value={quikqStartCode}
                        onChange={(e) => setQuikqStartCode(e.target.value)}
                        sx={{ flex: '1 1 180px' }}
                      />
                    </Box>
                  </TabPanel>

                  <TabPanel value={searchTab} index={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Manual URL (overrides path + query table)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={rawUrlOverride}
                      onChange={(e) => setRawUrlOverride(e.target.value)}
                      placeholder="https://api.quikq.com/v1/carriers/stations?startCode=…"
                      helperText="Paste the exact URL from Postman (must include startCode and location params as needed)."
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="subtitle2" gutterBottom>
                      Filter results (client-side)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={clientListFilter}
                      onChange={(e) => setClientListFilter(e.target.value)}
                      placeholder="Substring in name, address, city…"
                      disabled={!searchResults?.length}
                    />
                  </TabPanel>
                </Box>
              </Paper>

              {searchError && <Alert severity="error">{searchError}</Alert>}

              {quikqSearchMeta && (
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Response · {filteredStationResults.length} station
                    {filteredStationResults.length === 1 ? '' : 's'}
                    {clientListFilter.trim() && searchResults.length !== filteredStationResults.length
                      ? ` (of ${searchResults.length} total)`
                      : ''}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                    GET: {quikqSearchMeta.endpoint}
                    {quikqSearchMeta.authMode === 'token'
                      ? ' · auth: Bearer token'
                      : quikqSearchMeta.authMode === 'request'
                        ? ' · auth: login form'
                        : quikqSearchMeta.authMode === 'env'
                          ? ' · auth: server env'
                          : ''}
                  </Typography>

                  {filteredStationResults.length === 0 && (
                    <Alert severity="info" sx={{ mb: 0 }}>
                      {searchResults.length === 0
                        ? 'No stations returned for this request.'
                        : 'No stations match the list filter. Clear or change the filter (Advanced tab).'}
                    </Alert>
                  )}

                  {filteredStationResults.length > 0 && (
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell width={48} />
                            <TableCell>Code</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>City</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell align="right">Miles</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredStationResults.map((s, idx) => {
                            const rowKey = s.stationCode ? `${s.stationCode}-${idx}` : `st-${idx}`;
                            const fuels = Array.isArray(s.fuelPrices) ? s.fuelPrices : [];
                            return (
                              <React.Fragment key={rowKey}>
                                <TableRow hover>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleStationRow(rowKey)}
                                      aria-label="expand repricing"
                                    >
                                      {expandedStationRows[rowKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>{s.stationCode}</TableCell>
                                  <TableCell>{s.stationName}</TableCell>
                                  <TableCell>
                                    {s.city}, {s.state}
                                  </TableCell>
                                  <TableCell>{s.address}</TableCell>
                                  <TableCell align="right">
                                    {s.miles != null ? Number(s.miles).toFixed(2) : '—'}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                    <Collapse in={!!expandedStationRows[rowKey]} timeout="auto" unmountOnExit>
                                      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          QuikQ repricing (same <code>repricing</code> array as in Postman). Pick a row,
                                          then enter override price below.
                                        </Typography>
                                        {fuels.length === 0 ? (
                                          <Box sx={{ py: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                              No repricing rows on this station. Use{' '}
                                              <strong>Select station only</strong> and choose a fuel product below.
                                            </Typography>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() => applyStationFuelSelection(s, null)}
                                            >
                                              Select station only
                                            </Button>
                                          </Box>
                                        ) : (
                                          <TableContainer
                                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
                                          >
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                  <TableCell>Prod</TableCell>
                                                  <TableCell>Fuel</TableCell>
                                                  <TableCell align="right">Retail</TableCell>
                                                  <TableCell align="right">Adj.</TableCell>
                                                  <TableCell>Type</TableCell>
                                                  <TableCell align="right">Current</TableCell>
                                                  <TableCell align="center">Use</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {fuels.map((fuel) => (
                                                  <TableRow key={`${rowKey}-fp-${fuel.prodCode}`} hover>
                                                    <TableCell>{fuel.prodCode}</TableCell>
                                                    <TableCell>{fuel.fuelName}</TableCell>
                                                    <TableCell align="right">{formatMoney(fuel.retailPrice)}</TableCell>
                                                    <TableCell align="right">{formatMoney(fuel.adjPrice)}</TableCell>
                                                    <TableCell>{fuel.adjType || '—'}</TableCell>
                                                    <TableCell align="right">
                                                      {fuel.currentPrice != null ? formatMoney(fuel.currentPrice) : '—'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => applyStationFuelSelection(s, fuel)}
                                                      >
                                                        Select
                                                      </Button>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        )}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Station JSON (QuikQ)
                                        </Typography>
                                        <Box
                                          component="pre"
                                          sx={{
                                            mt: 0.5,
                                            fontSize: 11,
                                            maxHeight: 220,
                                            overflow: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            bgcolor: 'background.paper',
                                            p: 1,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                          }}
                                        >
                                          {JSON.stringify(s, null, 2)}
                                        </Box>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              )}

          <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {overrideDetailsFields}
          </Paper>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', pt: 1 }}>
            <Button variant="outlined" onClick={() => setUiTab(0)}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSaveOverride}>
              Create
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Price Override</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {overrideDetailsFields}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSaveOverride}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PriceOverrides;
